'use client';

import { Radio } from '@pickleballinc/react-ui';
import { useForm, FormProvider, Controller } from 'react-hook-form';

import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { MatchupCompletedEnum, MatchupCompletedEnumTypes, MatchupStatusEnum } from '@/domain/constants/constants';

interface EditMatchupStatusModalProps {
	teamOneTitle: string;
	teamTwoTitle: string;
	onClose: () => void;
	onConfirm: (status: number) => void;
	onUndo: () => void;
	isSubmitting: boolean;
	matchupStatus: string;
	matchupCompletedType: string;
}

interface MatchupStatusFormData {
	status: number;
}

export const EditMatchupStatusModal = ({
	teamOneTitle,
	teamTwoTitle,
	onClose,
	onConfirm,
	onUndo,
	isSubmitting,
	matchupStatus,
	matchupCompletedType
}: EditMatchupStatusModalProps) => {
	let defaultStatus =
		matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
			? MatchupCompletedEnumTypes.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
			: matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
				? MatchupCompletedEnumTypes.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
				: matchupCompletedType === MatchupCompletedEnum.CANCELED_MATCHUP_COMPLETION_TYPE
					? MatchupCompletedEnumTypes.CANCELED_MATCHUP_COMPLETION_TYPE
					: -1;

	if (matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) {
		defaultStatus = -1;
	}

	const form = useForm<MatchupStatusFormData>({
		defaultValues: {
			status: defaultStatus
		}
	});

	const { handleSubmit, control, watch } = form;
	const currentStatus = watch('status');
	const hasStatusChanged = currentStatus !== defaultStatus;

	const handleFormSubmit = handleSubmit((data: MatchupStatusFormData) => {
		if (data.status === -1) {
			onUndo();
		} else {
			onConfirm(data.status);
		}
	});

	return (
		<ModalWrapper title="Matchup Status" onClose={onClose} className="max-w-[450px]">
			<hr className="text-gray-400 my-4" />
			<FormProvider {...form}>
				<form onSubmit={handleFormSubmit} className="flex flex-col gap-4 px-2 sm:px-16">
					<Controller
						control={control}
						name="status"
						render={({ field }) => {
							const options = [
								{
									value: -1,
									label: 'None'
								},
								{
									value: MatchupCompletedEnumTypes.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE,
									label: `${teamTwoTitle} (A) withdraw`
								},
								{
									value: MatchupCompletedEnumTypes.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE,
									label: `${teamOneTitle} (H) withdraw`
								},
								{
									value: MatchupCompletedEnumTypes.CANCELED_MATCHUP_COMPLETION_TYPE,
									label: 'Cancel matchup'
								}
							];

							return (
								<div className="flex flex-col gap-2">
									{options.map(({ value, label }) => (
										<div key={value} className="bg-gray-100 rounded-lg px-3 py-2 cursor-pointer">
											<Radio
												radioTheme="orange"
												type="radio"
												name={field.name}
												value={value}
												checked={field.value === value}
												onChange={() => field.onChange(value)}
												className="size-5 text-gray-900 text-sm cursor-pointer"
												Text={label}
											/>
										</div>
									))}
								</div>
							);
						}}
					/>

					<ActionButtons
						className={'pt-10'}
						onClose={onClose}
						submitButtonText="Save"
						closeButtonText="Close"
						submitButtonClassName="w-full bg-orange-500 hover:bg-orange-700 text-white"
						closeButtonClassName="w-full !text-darkorange-600"
						submitButtonDisabled={isSubmitting || !hasStatusChanged}
						submitTooltipMessage={!hasStatusChanged ? 'Please select a different status to save changes' : undefined}
					/>
				</form>
			</FormProvider>
		</ModalWrapper>
	);
};
